import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { UsersFour, MapPin, Clock, User, CheckCircle, ArrowSquareOut, X, Ticket, HourglassMedium } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";

const SYM = { usd: "$", gbp: "£", eur: "€", cad: "$", aud: "$", nzd: "$", inr: "₹", jpy: "¥" };
const money = (n, c) => `${SYM[(c || "gbp").toLowerCase()] || ""}${Number(n || 0).toFixed(2)}`;
const dayLabel = (iso) => new Date(iso).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
const timeLabel = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

export default function ClientGroupClasses() {
  const [data, setData] = useState(null);
  const [booking, setBooking] = useState(null);

  const load = () => api.get("/client/group/classes").then(({ data }) => setData(data)).catch(() => setData({ enabled: false, classes: [] }));
  useEffect(() => { load(); }, []);
  const [myPacks, setMyPacks] = useState([]);
  const loadPacks = () => api.get("/client/group/packs/mine").then(({ data }) => setMyPacks(data)).catch(() => {});
  useEffect(() => { loadPacks(); }, []);
  const [paidResult, setPaidResult] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const bs = params.get("booking_session"), ps = params.get("pack_session");
    if (!bs && !ps) return;
    window.history.replaceState({}, "", window.location.pathname);
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(bs ? `/client/group/booking-status/${bs}` : `/client/group/pack-status/${ps}`);
        if (data.status === "paid") { setPaidResult({ kind: bs ? "booking" : "pack", ...data }); load(); loadPacks(); return; }
      } catch (_e) { /* keep polling */ }
      if (tries < 8) setTimeout(poll, 2000); else setPaidResult({ kind: bs ? "booking" : "pack", status: "pending" });
    };
    poll();
  }, []);
  const buyPack = async (p) => {
    try {
      const { data } = await api.post(`/client/group/packs/${p.id}/buy`, { origin_url: window.location.origin });
      if (data.pay_url) window.location.href = data.pay_url;
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const cancel = async (c) => {
    if (!window.confirm(`Cancel your spot in ${c.title}?`)) return;
    try {
      await api.post(`/client/group/bookings/${c.my_booking.id}/cancel`);
      toast.success("Spot released");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const joinWaitlist = async (c) => {
    try {
      const { data } = await api.post(`/client/group/classes/${c.id}/waitlist`);
      toast.success(`You're #${data.position} on the waitlist — you'll be booked in automatically and emailed when a spot opens`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const leaveWaitlist = async (c) => {
    try {
      await api.post(`/client/group/waitlist/${c.my_waitlist.id}/leave`);
      toast.success("Left the waitlist");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const grouped = useMemo(() => {
    const map = new Map();
    for (const c of data?.classes || []) {
      const key = (c.start || "").slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    return [...map.entries()];
  }, [data]);

  if (!data) return <div className="card-flat p-16 text-center text-zinc-500 tracker">Loading…</div>;

  if (!data.enabled) {
    return (
      <div className="card-flat p-16 text-center" data-testid="client-group-disabled">
        <UsersFour size={32} weight="duotone" className="text-brand mx-auto mb-3" />
        <div className="font-display text-2xl mb-2">No group classes yet.</div>
        <p className="text-zinc-500 text-sm">Your coach hasn't opened up group sessions. Check back soon.</p>
      </div>
    );
  }

  const mine = data.classes.filter((c) => c.my_booking);

  return (
    <div className="space-y-8" data-testid="client-group-classes">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="tracker text-brand mb-3">[ Group classes ]</div>
          <h1 className="font-display text-4xl sm:text-5xl">Join a class.</h1>
          <p className="text-zinc-500 mt-2">Book a spot in {data.brand_name}'s group sessions — your details are already filled in.</p>
        </div>
        {data.public_url && (
          <a href={data.public_url} target="_blank" rel="noopener noreferrer" className="btn-ghost !py-2 !px-3 !text-xs" data-testid="client-group-public-link">
            <ArrowSquareOut size={14} /> Share with a friend
          </a>
        )}
      </div>

      {paidResult && (
        <div className="card-flat p-5 border-emerald-500/30" data-testid="client-group-payment-result">
          {paidResult.status === "paid" ? (
            <>
              <div className="tracker text-emerald-400 text-[10px] mb-1">PAYMENT CONFIRMED</div>
              {paidResult.kind === "booking"
                ? <p className="text-sm">You're booked into <strong>{paidResult.class_title}</strong> — confirmation is in your inbox.</p>
                : <p className="text-sm">{paidResult.pack_name} unlocked · your code <span className="font-mono text-brand-light" data-testid="client-group-pack-code">{paidResult.code}</span> ({paidResult.credits} credits) — use it when booking.</p>}
            </>
          ) : <p className="text-sm text-zinc-400">Payment is processing — we'll email you the moment it lands.</p>}
        </div>
      )}

      {(data.packs?.length > 0 || myPacks.length > 0 || data.credits_available > 0) && (
        <div className="card-flat p-5" data-testid="client-group-packs">
          {data.credits_available > 0 && (
            <div className="mb-4 flex items-center gap-3" data-testid="client-group-credits-balance">
              <Ticket size={22} weight="duotone" className="text-emerald-400" />
              <div><div className="font-display text-2xl">{data.credits_available} class credit{data.credits_available === 1 ? "" : "s"}</div><div className="tracker text-[10px] text-zinc-500">USED AUTOMATICALLY WHEN YOU BOOK — NOTHING TO PAY</div></div>
            </div>
          )}
          <div className="tracker text-zinc-500 text-[10px] mb-3">CLASS PACKS — pay once, book with a code</div>
          <div className="flex flex-wrap gap-3">
            {(data.packs || []).map((p) => (
              <button key={p.id} onClick={() => buyPack(p)} className="border border-white/10 hover:border-brand/60 px-4 py-3 text-left transition-colors" data-testid={`client-group-buy-pack-${p.id}`}>
                <div className="font-display text-lg">{p.name}</div>
                <div className="tracker text-[10px] text-zinc-500">{p.credits} credits · <span className="text-zinc-200 money">{money(p.price, p.currency)}</span> · Buy →</div>
              </button>
            ))}
          </div>
          {myPacks.length > 0 && (
            <div className="mt-4 space-y-1" data-testid="client-group-my-packs">
              <div className="tracker text-zinc-500 text-[10px]">YOUR CODES</div>
              {myPacks.map((p) => (
                <div key={p.id} className="text-xs flex items-center gap-3"><span className="font-mono text-brand-light">{p.code}</span><span className="text-zinc-400">{p.pack_name} · {p.credits_left}/{p.credits} left</span></div>
              ))}
            </div>
          )}
        </div>
      )}

      {mine.length > 0 && (
        <div className="card-flat p-5" data-testid="client-group-mine">
          <div className="tracker text-zinc-500 text-[10px] mb-3">YOUR UPCOMING SPOTS ({mine.length})</div>
          <div className="flex flex-wrap gap-2">
            {mine.map((c) => (
              <span key={c.id} className="tracker text-[10px] px-2.5 py-1.5 border border-brand/40 text-brand-light inline-flex items-center gap-1.5" data-testid={`client-group-mine-${c.id}`}>
                <CheckCircle size={11} weight="fill" /> {c.title} · {timeLabel(c.start)} {new Date(c.start).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
              </span>
            ))}
          </div>
        </div>
      )}

      {grouped.length === 0 ? (
        <div className="card-flat p-12 text-center" data-testid="client-group-empty">
          <p className="font-display text-2xl mb-2">No upcoming classes.</p>
          <p className="text-zinc-500 text-sm">New sessions are added regularly — check back soon.</p>
        </div>
      ) : (
        <div className="space-y-8" data-testid="client-group-list">
          {grouped.map(([day, classes]) => (
            <div key={day}>
              <h2 className="tracker text-zinc-500 text-[10px] mb-3">{dayLabel(classes[0].start).toUpperCase()}</h2>
              <div className="space-y-3">
                {classes.map((c) => (
                  <article key={c.id} className="card-flat p-5 flex items-center justify-between gap-4 flex-wrap" data-testid={`client-group-class-${c.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-display text-xl text-brand">{timeLabel(c.start)}</span>
                        <h3 className="font-display text-xl">{c.title}</h3>
                        {c.instructor_name && <span className="text-zinc-400 text-sm inline-flex items-center gap-1"><User size={12} /> with {c.instructor_name}</span>}
                      </div>
                      <div className="flex items-center gap-4 mt-1.5 text-zinc-500 text-xs flex-wrap">
                        <span className="inline-flex items-center gap-1"><Clock size={12} /> {c.duration_min} min</span>
                        {c.location && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {c.location}</span>}
                        <span className="money text-zinc-300">{money(c.price, c.currency)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {c.my_booking ? (
                        <>
                          <span className="tracker text-[9px] px-2 py-1 border border-emerald-500/40 text-emerald-400" data-testid={`client-group-booked-${c.id}`}>
                            {c.my_booking.status === "paid" ? "BOOKED · PAID" : "BOOKED"}
                          </span>
                          <button onClick={() => cancel(c)} className="btn-ghost !py-2 !px-3 !text-xs" data-testid={`client-group-cancel-${c.id}`}>Cancel</button>
                        </>
                      ) : c.my_waitlist ? (
                        <>
                          <span className="tracker text-[9px] px-2 py-1 border border-amber-500/40 text-amber-400" data-testid={`client-group-waitlisted-${c.id}`}>
                            WAITLIST #{c.my_waitlist.position} · AUTO-BOOKED WHEN A SPOT FREES
                          </span>
                          <button onClick={() => leaveWaitlist(c)} className="btn-ghost !py-2 !px-3 !text-xs" data-testid={`client-group-leave-waitlist-${c.id}`}>Leave</button>
                        </>
                      ) : (
                        <>
                          <span className={`tracker text-[9px] px-2 py-1 border ${c.spots_left === 0 ? "border-red-500/40 text-red-400" : "border-white/10 text-zinc-400"}`} data-testid={`client-group-spots-${c.id}`}>
                            {c.spots_left === 0 ? `FULL${c.waitlist_count ? ` · ${c.waitlist_count} WAITING` : ""}` : `${c.spots_left} LEFT`}
                          </span>
                          {c.spots_left === 0 ? (
                            <button onClick={() => joinWaitlist(c)} className="btn-ghost !py-2 !px-4 !text-xs" data-testid={`client-group-waitlist-${c.id}`}>
                              <HourglassMedium size={12} /> Join waitlist
                            </button>
                          ) : (
                            <button onClick={() => setBooking(c)} className="btn-primary !py-2 !px-4 !text-xs" data-testid={`client-group-book-${c.id}`}>Book</button>
                          )}
                        </>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {booking && <BookModal cls={booking} credits={data.credits_available || 0} onClose={() => setBooking(null)} onBooked={load} />}
    </div>
  );
}

function BookModal({ cls, credits, onClose, onBooked }) {
  const [method, setMethod] = useState("stripe");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const hasCredits = credits > 0;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/client/group/classes/${cls.id}/book`, { method, pack_code: method === "pack" ? code : null, origin_url: window.location.origin });
      if (data.pay_url) { window.location.href = data.pay_url; return; }
      setResult(data);
      onBooked();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-flat w-full max-w-md p-8" onClick={(e) => e.stopPropagation()} data-testid="client-group-book-modal">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="tracker text-brand mb-2">[ Book a spot ]</div>
            <h2 className="font-display text-3xl">{cls.title}</h2>
            <p className="tracker text-zinc-500 text-[10px] mt-1">{dayLabel(cls.start)} · {timeLabel(cls.start)}</p>
          </div>
          <button onClick={onClose} data-testid="client-group-book-close"><X size={20} weight="bold" /></button>
        </div>
        {result ? (
          <div className="space-y-4" data-testid="client-group-book-success">
            <CheckCircle size={40} weight="fill" className="text-emerald-400" />
            <p className="font-display text-2xl">You're in.</p>
            <p className="text-zinc-400 text-sm">{result.note}</p>
            {result.pay_url && (
              <a href={result.pay_url} target="_blank" rel="noopener noreferrer" className="btn-primary w-full justify-center" data-testid="client-group-pay-link">Pay now <ArrowSquareOut size={14} /></a>
            )}
            <button onClick={onClose} className="btn-ghost w-full justify-center" data-testid="client-group-book-done">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {hasCredits ? (
              <div className="border border-emerald-500/40 bg-emerald-500/5 px-4 py-3 text-sm flex items-center gap-3" data-testid="client-group-credits-notice">
                <Ticket size={18} weight="duotone" className="text-emerald-400 shrink-0" />
                <span>You have <strong className="text-emerald-400">{credits} class credit{credits === 1 ? "" : "s"}</strong> — this booking uses 1, nothing to pay.</span>
              </div>
            ) : (
              <div className="flex gap-1">
                <button type="button" onClick={() => setMethod("stripe")} className={`tracker px-3 py-1.5 border ${method === "stripe" ? "border-brand text-white" : "border-white/10 text-zinc-500"}`} data-testid="client-group-method-stripe">
                  Pay by card now
                </button>
                <button type="button" onClick={() => setMethod("pack")} className={`tracker px-3 py-1.5 border ${method === "pack" ? "border-brand text-white" : "border-white/10 text-zinc-500"}`} data-testid="client-group-method-pack">
                  <Ticket size={11} className="inline -mt-0.5 mr-1" />Use a pack code
                </button>
              </div>
            )}
            {!hasCredits && method === "pack" && (
              <input required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="PK-XXXXXX" className="w-full px-4 py-3 border font-mono" data-testid="client-group-pack-code" />
            )}
            <p className="text-zinc-500 text-xs">{hasCredits ? "Your spot is confirmed instantly. Confirmation goes to your account email." : "Paid upfront — your spot is confirmed the moment payment lands. Confirmation goes to your account email."}</p>
            <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="client-group-book-submit">
              {loading ? "Booking…" : hasCredits ? "Book · use 1 credit" : `Book · ${money(cls.price, cls.currency)}`}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
